import { useState, useEffect, useRef } from "react";

const NotFound: React.FC = () => {
  const [stars, setStars] = useState<
    { id: number; x: number; y: number; size: number; delay: number; duration: number }[]
  >([]);
  const [isMounted, setIsMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const generated = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2.5 + 0.8,
      delay: Math.random() * 4,
      duration: Math.random() * 3 + 2,
    }));
    setStars(generated);
    requestAnimationFrame(() => setIsMounted(true));
  }, []);

  return (
    <div
      ref={containerRef}
      className="container h-full rounded-none"
      style={{
        background: "radial-gradient(ellipse at 30% 20%, #0f0c29 0%, #090714 40%, #030208 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative",
        fontFamily: "'Outfit', sans-serif",
        padding: "24px 20px",
        boxSizing: "border-box",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Outfit:wght@200;400;700;900&family=Space+Mono:wght@400;700&display=swap"
        rel="stylesheet"
      />

      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.4); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-14px) rotate(2deg); }
          75% { transform: translateY(8px) rotate(-1.5deg); }
        }
        @keyframes drift {
          0% { transform: translateX(-60vw) translateY(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(100vw) translateY(-30px); opacity: 0; }
        }
        @keyframes glitch {
          0%, 100% { text-shadow: 1.5px 0 #ff004c, -1.5px 0 #00f0ff; }
          25% { text-shadow: -1.5px -1px #ff004c, 1.5px 1px #00f0ff; }
          50% { text-shadow: 1px 1.5px #ff004c, -1px -1.5px #00f0ff; }
          75% { text-shadow: -1px 1px #ff004c, 1px -1px #00f0ff; }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 16px rgba(120, 80, 255, 0.25); }
          50% { box-shadow: 0 0 32px rgba(120, 80, 255, 0.5); }
        }
        @keyframes orbFloat {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(6px, -10px); }
          66% { transform: translate(-4px, 5px); }
        }

        .nf-back-btn {
          font-family: 'Space Mono', monospace;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.85);
          background: rgba(120, 80, 255, 0.12);
          border: 1px solid rgba(120, 80, 255, 0.35);
          padding: 14px 28px;
          border-radius: 50px;
          text-decoration: none;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          transition: all 0.3s ease;
          animation: pulse 3s ease-in-out infinite;
          -webkit-backdrop-filter: blur(10px);
          backdrop-filter: blur(10px);
          display: inline-block;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          min-height: 48px;
          min-width: 48px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .nf-back-btn:active {
          transform: scale(0.96);
          background: rgba(120, 80, 255, 0.2);
        }

        /* Tablet+ */
        @media (min-width: 480px) {
          .nf-back-btn {
            font-size: 13px;
            padding: 15px 36px;
          }
        }

        /* Desktop */
        @media (min-width: 768px) {
          .nf-back-btn {
            font-size: 14px;
            padding: 16px 40px;
          }
          .nf-back-btn:hover {
            background: rgba(120, 80, 255, 0.25);
            transform: translateY(-2px);
            box-shadow: 0 8px 32px rgba(120, 80, 255, 0.4);
          }
          .nf-back-btn:active {
            transform: translateY(0);
          }
        }

        /* Safe area for notched phones */
        @supports (padding: env(safe-area-inset-bottom)) {
          .nf-container {
            padding-bottom: calc(24px + env(safe-area-inset-bottom));
          }
        }
      `}</style>

      {/* Stars */}
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            position: "absolute",
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size,
            borderRadius: "50%",
            background: "#fff",
            animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Shooting star */}
      <div
        style={{
          position: "absolute",
          top: "12%",
          width: 60,
          height: 1.5,
          background: "linear-gradient(90deg, transparent, #fff, transparent)",
          animation: "drift 9s linear infinite",
          animationDelay: "3s",
          borderRadius: 2,
          pointerEvents: "none",
        }}
      />

      {/* Nebula blurs */}
      <div
        style={{
          position: "absolute",
          width: "min(400px, 80vw)",
          height: "min(400px, 80vw)",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(120, 50, 255, 0.07) 0%, transparent 70%)",
          top: "5%",
          right: "-15%",
          filter: "blur(50px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "min(300px, 70vw)",
          height: "min(300px, 70vw)",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255, 50, 100, 0.05) 0%, transparent 70%)",
          bottom: "5%",
          left: "-10%",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />

      {/* Floating astronaut */}
      <div
        style={{
          animation: "float 6s ease-in-out infinite",
          marginBottom: "clamp(12px, 3vw, 28px)",
        }}
      >
        <div
          style={{
            opacity: isMounted ? 1 : 0,
            transform: isMounted ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <svg
            viewBox="0 0 120 140"
            fill="none"
            style={{ width: "clamp(70px, 18vw, 110px)", height: "auto", display: "block" }}
          >
            <ellipse cx="60" cy="45" rx="32" ry="35" fill="#1a1a2e" stroke="#444" strokeWidth="2" />
            <ellipse cx="60" cy="43" rx="24" ry="26" fill="url(#visor404)" />
            <ellipse cx="52" cy="38" rx="8" ry="12" fill="rgba(120,180,255,0.12)" transform="rotate(-10 52 38)" />
            <rect x="35" y="72" width="50" height="40" rx="12" fill="#1a1a2e" stroke="#444" strokeWidth="2" />
            <rect x="78" y="75" width="14" height="30" rx="5" fill="#151525" stroke="#333" strokeWidth="1.5" />
            <rect
              x="18"
              y="78"
              width="20"
              height="10"
              rx="5"
              fill="#1a1a2e"
              stroke="#444"
              strokeWidth="1.5"
              transform="rotate(-15 28 83)"
            />
            <rect
              x="82"
              y="78"
              width="20"
              height="10"
              rx="5"
              fill="#1a1a2e"
              stroke="#444"
              strokeWidth="1.5"
              transform="rotate(15 92 83)"
            />
            <rect
              x="40"
              y="108"
              width="14"
              height="22"
              rx="6"
              fill="#1a1a2e"
              stroke="#444"
              strokeWidth="1.5"
              transform="rotate(-5 47 119)"
            />
            <rect
              x="62"
              y="108"
              width="14"
              height="22"
              rx="6"
              fill="#1a1a2e"
              stroke="#444"
              strokeWidth="1.5"
              transform="rotate(5 69 119)"
            />
            <circle cx="60" cy="88" r="4" fill="#7850ff" opacity="0.8">
              <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
            </circle>
            <defs>
              <linearGradient id="visor404" x1="36" y1="20" x2="84" y2="66">
                <stop offset="0%" stopColor="#1e90ff" stopOpacity="0.6" />
                <stop offset="50%" stopColor="#0a0a2a" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#7850ff" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* 404 */}
      <h1
        style={{
          fontFamily: "'Space Mono', monospace",
          fontSize: "clamp(64px, 20vw, 150px)",
          fontWeight: 700,
          color: "transparent",
          background: "linear-gradient(135deg, #7850ff 0%, #ff004c 50%, #00f0ff 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          margin: 0,
          lineHeight: 1,
          animation: "glitch 3s ease-in-out infinite",
          letterSpacing: "-0.02em",
          userSelect: "none",
          opacity: isMounted ? 1 : 0,
          transform: isMounted ? "translateY(0)" : "translateY(24px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s",
        }}
      >
        404
      </h1>

      {/* Subtitle */}
      <p
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 200,
          fontSize: "clamp(11px, 3vw, 20px)",
          color: "rgba(255, 255, 255, 0.55)",
          margin: "clamp(8px, 2vw, 16px) 0 clamp(4px, 1vw, 8px)",
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          textAlign: "center",
          opacity: isMounted ? 1 : 0,
          transform: isMounted ? "translateY(0)" : "translateY(24px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s",
        }}
      >
        Không tìm thấy trang
      </p>

      <p
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 400,
          fontSize: "clamp(13px, 3.2vw, 16px)",
          color: "rgba(255, 255, 255, 0.3)",
          margin: "0 0 clamp(24px, 5vw, 40px)",
          maxWidth: 340,
          textAlign: "center",
          lineHeight: 1.65,
          padding: "0 4px",
          opacity: isMounted ? 1 : 0,
          transform: isMounted ? "translateY(0)" : "translateY(24px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.45s",
        }}
      >
        Trang hoặc Chip bạn đang tìm kiếm không tồn tại trên hệ thống hoặc sai đường dẫn.
      </p>

      {/* Button */}
      {/* <div
        style={{
          opacity: isMounted ? 1 : 0,
          transform: isMounted ? "translateY(0)" : "translateY(24px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s",
        }}
      >
        <a href="/" className="nf-back-btn">
          ← Return Home
        </a>
      </div> */}

      {/* Floating orbs */}
      {[...Array(4)].map((_, i) => (
        <div
          key={`orb-${i}`}
          style={{
            position: "absolute",
            width: "clamp(3px, 1vw, 5px)",
            height: "clamp(3px, 1vw, 5px)",
            borderRadius: "50%",
            background: i % 2 === 0 ? "rgba(120, 80, 255, 0.4)" : "rgba(255, 0, 76, 0.3)",
            left: `${12 + i * 22}%`,
            bottom: `${8 + (i % 3) * 18}%`,
            animation: `orbFloat ${4 + i * 1.5}s ease-in-out ${i * 0.7}s infinite`,
            pointerEvents: "none",
          }}
        />
      ))}
    </div>
  );
};

export default NotFound;
